/*
   Fotos de ejemplo por grado (A-D) para las preguntas de cotización que lo requieren
   (pantalla, cuerpo/carcasa). La foto que se muestra depende de la respuesta que el
   usuario eligió para esa pregunta (a qué grado corresponde). Para abolladuras se usa
   siempre la misma foto, sin distinción de grado ni de respuesta. La pregunta de carcasa
   además incluye siempre la foto de la cámara.

   Para agregar/actualizar imágenes: subirlas a S3 en la carpeta indicada por
   QUESTION_ASSETS_BASE, respetando el nombre `<Categoria>+Grado+<Letra>.png`.
 */

const QUESTION_ASSETS_BASE = `https://${import.meta.env.VITE_BUCKET}.s3.us-west-2.amazonaws.com/assets/qa/`

const GRADOS = ['A', 'B', 'C', 'D']

const buildGradeImages = (categoria: string): string[] =>
  GRADOS.map(grado => `${QUESTION_ASSETS_BASE}${categoria}+Grado+${grado}.png`)

const pantallaImages = buildGradeImages('Pantalla')
const cuerpoImages = buildGradeImages('Cuerpo')
/* La abolladura y la cámara no distinguen grado: siempre se muestra la misma foto de referencia */
const abolladuraImage = `${QUESTION_ASSETS_BASE}Abolladura+Grado+A.png`
const camaraImage = `${QUESTION_ASSETS_BASE}Camara.png`

type TQuestionCategory = 'pantalla' | 'cuerpo' | 'abolladura'

/**
 * Detecta a qué categoría de fotos de ejemplo corresponde una pregunta según su texto,
 * o null si esa pregunta no tiene fotos de referencia asociadas.
 */
const getQuestionCategory = (questionText: any): TQuestionCategory | null => {
  if (typeof questionText !== 'string') return null
  const texto = questionText.toLowerCase()
  if (texto.includes('pantalla')) return 'pantalla'
  if (texto.includes('cuerpo') || texto.includes('carcasa')) return 'cuerpo'
  if (texto.includes('abolladura')) return 'abolladura'
  return null
}

export const hasQuestionExampleImage = (questionText: any): boolean => getQuestionCategory(questionText) !== null

/**
 * Devuelve las fotos de ejemplo correspondientes a la respuesta seleccionada.
 * Las respuestas de una pregunta se ordenan de mejor a peor estado según su `weigth`
 * (a menor weigth, mejor estado = grado A), y esa posición determina el grado (A-D)
 * que se muestra. Para abolladuras siempre se muestra la misma foto, sin importar la
 * respuesta (ni siquiera si ya se contestó). Para carcasa, además de la foto de grado
 * se incluye siempre la foto de la cámara.
 */
export const getQuestionExampleImages = (questionText: any, answers: Array<{ id: number, weigth: number }>, selectedAnswerId?: number): string[] | null => {
  const category = getQuestionCategory(questionText)
  if (!category) return null
  if (category === 'abolladura') return [abolladuraImage]

  const images = category === 'pantalla' ? pantallaImages : cuerpoImages
  const ordenado = [...answers].sort((a, b) => a.weigth - b.weigth)
  const idx = ordenado.findIndex(a => a.id === selectedAnswerId)
  if (idx === -1) return null

  const gradeImage = images[Math.min(idx, images.length - 1)]
  return category === 'cuerpo' ? [gradeImage, camaraImage] : [gradeImage]
}
